'use client';

/**
 * VoiceSettings — izbira TTS glasu + preizkusno polje (Nastavitve dialog).
 * Glasove vrne /api/voice/voices (Azure le, ko je strežnik konfiguriran);
 * izbira persistira v localStorage (lib/voice/voice-pref) in jo bere
 * SpeakButton. Preizkus uporabi isti dvostopenjski tok kot SpeakButton:
 * POST registrira besedilo → audio.src = GET ?id= + play() v klik handlerju
 * (Firefox user-activation past).
 */
import { useEffect, useRef, useState } from 'react';
import { Check, Cloud, HardDrive, Loader2, Mic, Square, Volume2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { getSelectedVoiceId, setSelectedVoiceId } from '@/lib/voice/voice-pref';
import type { VoiceDef } from '@/lib/voice/voices';

const SAMPLE_TEXT =
  'Skladno s prvim odstavkom 14. člena Zakona o javnem naročanju se ta določba uporablja od 1. marca 2026.';

type TestState = 'idle' | 'loading' | 'playing';

export function VoiceSettings() {
  const [voices, setVoices] = useState<VoiceDef[]>([]);
  const [selected, setSelected] = useState<string>('');
  const [sample, setSample] = useState(SAMPLE_TEXT);
  const [testState, setTestState] = useState<TestState>('idle');
  const [error, setError] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    setSelected(getSelectedVoiceId());
    fetch('/api/voice/voices', { cache: 'no-store' })
      .then((r) => r.json())
      .then((d) => {
        if (Array.isArray(d.voices)) setVoices(d.voices);
      })
      .catch(() => setError('Seznama glasov ni bilo mogoče naložiti.'));
    return () => stopAudio();
  }, []);

  const stopAudio = () => {
    const audio = audioRef.current;
    audioRef.current = null;
    if (audio) {
      // handlerje odklopi PRED pause/src='' — sicer sproži lažni onerror
      audio.onplaying = null;
      audio.onended = null;
      audio.onerror = null;
      audio.pause();
      audio.src = '';
      audio.load();
    }
    setTestState('idle');
  };

  const choose = (id: string) => {
    setSelected(id);
    setSelectedVoiceId(id);
  };

  const test = async () => {
    if (testState !== 'idle') {
      stopAudio();
      return;
    }
    setError(null);
    setTestState('loading');
    try {
      const res = await fetch('/api/voice/tts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: sample.trim() || SAMPLE_TEXT, voice: selected }),
      });
      if (!res.ok) throw new Error(`TTS ${res.status}`);
      const { id } = (await res.json()) as { id?: string };
      if (!id) throw new Error('TTS: odgovor brez id');

      const audio = new Audio();
      audioRef.current = audio;
      audio.onplaying = () => setTestState('playing');
      audio.onended = () => stopAudio();
      audio.onerror = () => {
        setError('Predvajanje ni uspelo — preveri TTS storitev (Stanje storitev spodaj).');
        stopAudio();
      };
      audio.src = `/api/voice/tts?id=${encodeURIComponent(id)}`;
      await audio.play();
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      setError(err instanceof Error ? err.message : 'Preizkus ni uspel');
      stopAudio();
    }
  };

  const cloudSelected = voices.find((v) => v.id === selected)?.cloud;

  return (
    <div className="space-y-3">
      <Label className="text-sm font-medium flex items-center gap-2">
        <Mic className="h-4 w-4" />
        Glas pomočnika (Poslušaj)
      </Label>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {voices.map((v) => {
          const isSelected = selected === v.id;
          return (
            <button
              key={v.id}
              type="button"
              onClick={() => choose(v.id)}
              className={cn(
                'relative flex items-center gap-2.5 p-2.5 rounded-lg border-2 transition-all text-left',
                'hover:bg-muted/50',
                isSelected ? 'border-primary bg-primary/5' : 'border-transparent bg-muted/30'
              )}
            >
              {v.cloud ? (
                <Cloud className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              ) : (
                <HardDrive className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{v.label.split(' (')[0]}</div>
                <div className="text-[10px] text-muted-foreground truncate">
                  {v.cloud ? 'Azure, oblak' : 'Piper, lokalni'}
                </div>
              </div>
              {isSelected && <Check className="h-4 w-4 text-primary flex-shrink-0" />}
            </button>
          );
        })}
        {voices.length === 0 && !error && (
          <span className="text-xs text-muted-foreground col-span-full py-2">Nalaganje glasov…</span>
        )}
      </div>

      {cloudSelected && (
        <p className="text-[11px] text-amber-600 dark:text-amber-500">
          Oblačni glas: besedilo odgovora se ob predvajanju pošlje Microsoftu (Azure, EU regija).
        </p>
      )}

      <div className="flex items-end gap-2">
        <div className="flex-1 space-y-1">
          <span className="text-xs text-muted-foreground">Preizkusno besedilo</span>
          <input
            value={sample}
            onChange={(e) => setSample(e.target.value)}
            className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm"
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          className="h-9 gap-1.5"
          disabled={voices.length === 0}
          onClick={test}
        >
          {testState === 'loading' ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : testState === 'playing' ? (
            <Square className="h-3.5 w-3.5" />
          ) : (
            <Volume2 className="h-3.5 w-3.5" />
          )}
          {testState === 'idle' ? 'Preizkusi' : 'Ustavi'}
        </Button>
      </div>

      {error && <p className="text-xs text-red-600 dark:text-red-500">{error}</p>}
      <p className="text-[10px] text-muted-foreground">
        Izbira velja za ta brskalnik in gumb »Poslušaj« pri AI odgovorih. Azure glasova se
        pojavita, ko je na strežniku nastavljen AZURE_SPEECH_KEY.
      </p>
    </div>
  );
}
